/* eslint-disable no-underscore-dangle */
/* eslint-disable jsx-a11y/anchor-is-valid */
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import queryString from 'query-string';
import swal from 'sweetalert';

import { SignInAction, UpdateTokenAction } from '../../store/actions/auth.actions';
import { setCountryAction } from '../../store/actions/country.actions';

import { loginService, pointsCounterService } from '../../services/user';
import { resetRegisterEmail } from '../../services/notifications';

import { setCurrentUser, encriptData } from '../../utils/auth';
import { operationValidate } from '../../utils/role';
import { redirectToOtherPlatforms } from '../../utils/intercommunication';
import { useValidationCountry } from '../../utils/validationCountry';

import Input from '../input';
import Button from '../button';
import Checkbox from '../checkBox';
import Typography from '../typography';
import ButtonsSocialMedia from '../buttonsLoginSocialMedia';
import Setup2FA from './setup2FA';

import NoEmail from '../../assets/images/email.png';
import IconVisibility from '../../assets/images/icon-visibility-color-gray.svg';
import IconVisibilityOff from '../../assets/images/icon-visibility-off-color-primary.svg';

import './login.scss';
import { operationsUrl } from '../../constants';
import OtpBoxes from '../otpBoxes';

const getUserPoints = async (userId, token) => {
  try {
    const responsePoints = await pointsCounterService(userId, token);
    return parseInt(responsePoints?.points ?? 0, 10);
  } catch (error) {
    return null;
  }
};

const Login = ({
  next, close,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobileDevice = useSelector((state) => state.mediaQuery.isMobileDevice);

  const [email, setEmail] = useState(null);
  const [password, setPassword] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [executingAction, setExecutingAction] = useState();
  const [errorLogin, setErrorLogin] = useState();
  const [urlToRedirect, setUrlToRedirect] = useState(null);

  const [twoFAStep, setTwoFAStep] = useState('login');
  const [twoFAData, setTwoFAData] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [verifyError, setVerifyError] = useState(null);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  const isCountryValid = useValidationCountry('170');

  useEffect(() => {
    const queryParams = queryString.parse(window.location.search);
    const intercommunicationData = queryParams.intercommunicationData
      ? JSON.parse(queryParams.intercommunicationData) : {};
    if (intercommunicationData.urlToReturnAfterLogin) {
      setUrlToRedirect(intercommunicationData.urlToReturnAfterLogin);
    }
  }, []);

  const processSuccessfulLogin = async (response, storedPassword) => {
    response.user.password = storedPassword;
    const points = await getUserPoints(response.user._id, response.token);
    response.user = {
      ...response.user,
      ...!points && { points },
    };
    dispatch(setCountryAction(response.user?.countryCode));
    await setCurrentUser(response, remember);
    const tokenEncrypt = await encriptData(response);
    if (urlToRedirect) {
      redirectToOtherPlatforms({ urlToRedirect, token: tokenEncrypt, keep: remember });
      return null;
    }
    dispatch(UpdateTokenAction(tokenEncrypt));
    dispatch(SignInAction({ ...response.user, points }));
    if (operationValidate({ ...response.user, points })) {
      redirectToOtherPlatforms({ urlToRedirect: `${operationsUrl}/buscar-codigo-mp`, token: tokenEncrypt, keep: remember });
      return null;
    }
    next();
    return null;
  };

  const loginFunction = async () => {
    setExecutingAction(true);
    setErrorLogin(null);
    try {
      const response = await loginService({ email: email.trim().toLowerCase(), password });

      if (response?.needSetUp) {
        setTwoFAData({
          email: email.trim().toLowerCase(),
          password,
          qr: response.qr,
          secret: response.secret,
        });
        setTwoFAStep('setup');
        return null;
      }

      if (response?.need2FA) {
        setTwoFAData({ email: email.trim().toLowerCase(), password });
        setOtpCode('');
        setVerifyError(null);
        setTwoFAStep('verify');
        return null;
      }

      if (response?.user?.accountEnabled) {
        await processSuccessfulLogin(response, password);
        return null;
      }

      swal({
        title: '¡Hola!',
        text: 'Aún no has activado tu cuenta. Revisa la bandeja de correos o la carpeta "spam" para encontrar el correo de activación.',
        icon: NoEmail,
        buttons: {
          cancel: 'Entendido',
          confirm: {
            text: 'Enviar correo nuevamente',
            value: 'search',
          },
        },
      }).then((value) => {
        const { user } = response;
        if (value === 'search' && user?._id) {
          resetRegisterEmail(user._id).then(() => {
            swal({
              title: '¡Enviado con exito!',
              text: 'Fué enviado nuevamente el correo de confirmación a tu correo',
              button: 'Entendido',
            });
          }).catch(() => {
            swal({
              title: 'Hubo un error',
              text: 'No pudo hacerse el envío por favor intentarlo de nuevo',
              button: 'Entendido',
            });
          });
        }
      });
    } catch (e) {
      setErrorLogin('Credenciales incorrectas.');
    } finally {
      setExecutingAction(false);
    }
    return null;
  };

  const verifyOtpFunction = async () => {
    if (otpCode.length !== 6) return;
    setVerifyingOtp(true);
    setVerifyError(null);
    try {
      const { email: storedEmail, password: storedPassword } = twoFAData;
      const response = await loginService({
        email: storedEmail,
        password: storedPassword,
        otpCode,
      });
      if (response?.user?.accountEnabled) {
        await processSuccessfulLogin(response, storedPassword);
      }
    } catch {
      setVerifyError('Código incorrecto. Intenta de nuevo.');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleBackToLogin = () => {
    setTwoFAStep('login');
    setTwoFAData(null);
    setOtpCode('');
    setVerifyError(null);
  };

  return (
    <div id="login" className={isMobileDevice ? 'mobile-mode' : `desktop-mode ${twoFAStep}`}>
      <div className="second-container__login">

        {twoFAStep === 'login' && (
          <>
            <div className={`header-login ${close ? 'with-svg' : 'without-svg'}`}>
              <div />
              <Typography text="Ingresa con" type="superTitle-italic" className="title-login" />
              {close && <div className="close-login" onClick={close} />}
            </div>

            <div className="form__login">
              <ButtonsSocialMedia urlToRedirect={urlToRedirect} remember={remember} />

              <div className="separator">
                <hr />
                <span>o</span>
                <hr />
              </div>

              <Input
                label="Correo electrónico"
                placeholder="usuario@gmail.com"
                value={email}
                onChange={setEmail}
              />
              <Input
                label="Escribe tu contraseña"
                placeholder="••••••••••"
                value={password}
                type={showPassword ? 'text' : 'password'}
                iconRigth={showPassword ? IconVisibilityOff : IconVisibility}
                onClickIconRight={() => setShowPassword(!showPassword)}
                onChange={setPassword}
                annotationError={errorLogin}
              />
              <Checkbox
                checked={remember}
                onChange={setRemember}
                text="Recordarme en este equipo"
              />
              <Button
                text="Ingresar"
                type="secondary"
                loading={executingAction}
                onClick={() => loginFunction()}
              />
              <a onClick={() => navigate('/validar-correo')}>¿Olvidaste tu contraseña?</a>
              <Button
                text="Crear cuenta"
                type={isCountryValid ? 'primary-mx' : 'primary'}
                onClick={() => navigate('/registro')}
              />
            </div>
          </>
        )}

        {twoFAStep === 'verify' && (
          <div className="verify2fa">
            <div className="shield_yes_icon">
              <div />
            </div>
            <h2 className="verify2fa__title">Verificación en dos pasos</h2>
            <p className="verify2fa__subtitle">
              Abre tu app de autenticación e ingresa el código de 6 dígitos
              que muestra para tu cuenta.
            </p>
            <OtpBoxes onChange={setOtpCode} />
            <p className="verify2fa__error">{verifyError || ''}</p>
            <div className="verify2fa__actions">
              <Button
                text="Verificar"
                type="primary"
                loading={verifyingOtp}
                disabled={otpCode.length !== 6}
                onClick={verifyOtpFunction}
              />
              <button
                type="button"
                className="back-link"
                onClick={handleBackToLogin}
              >
                Volver al inicio de sesión
              </button>
            </div>
          </div>
        )}

        {twoFAStep === 'setup' && twoFAData && (
          <Setup2FA
            email={twoFAData.email}
            password={twoFAData.password}
            qr={twoFAData.qr}
            secret={twoFAData.secret}
            onSetupComplete={handleBackToLogin}
          />
        )}

      </div>
    </div>
  );
};

Login.propTypes = {
  next: PropTypes.func.isRequired,
  close: PropTypes.func,
};

Login.defaultProps = {
  close: null,
};

export default Login;
